import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, Mail, Lock, ArrowRight, ChevronLeft, Check, GraduationCap, Sparkles, Globe } from "lucide-react";
import { T, PrimaryBtn, NeonInput, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";

/**
 * SIGNUP PREVIEW — EnglishTest.UK
 * Daylight Light theme + solid orange button + click sound + AI props
 * Left: brand + "Why join" benefits with checkmarks
 * Right: signup form with full name, email, password, target exam selector
 */
const TARGET_EXAMS = [
  { id: "IELTS",  name: "IELTS" },
  { id: "PTE",    name: "PTE" },
  { id: "ELLT",   name: "Oxford ELLT" },
  { id: "DUO",    name: "Duolingo" },
];

const BENEFITS = [
  "AI Speaking Examiner — real-time fluency & pronunciation scoring",
  "AI Writing Checker — full IELTS band-descriptor feedback",
  "Predicted band score with weakness heatmaps",
  "Paper-based & computer-based mock simulation",
  "24/7 AI Coach + optional live online classes",
];

export default function SignupPreview() {
  const play = useClickSound();
  const [exam, setExam] = useState("IELTS");

  useEffect(() => {
    const id = "lingoq-preview-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="min-h-screen relative grid grid-cols-1 lg:grid-cols-[1fr_1.05fr]" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="signup-preview-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* ============================================================
          LEFT — brand + benefits + AI props
      ============================================================ */}
      <div className="relative overflow-hidden p-8 lg:p-14 hidden lg:flex flex-col justify-between"
        style={{ background: `linear-gradient(135deg, ${T.bg2} 0%, ${T.cream}88 100%)`, borderRight: `1px solid ${T.border}` }}>
        <AIPropsBackground />

        <div className="relative">
          <AuthBrand />
        </div>

        <div className="relative">
          <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// NEW RECRUIT</div>
          <h1 className="display text-5xl lg:text-6xl font-bold leading-[1.0]" style={{ color: T.text }}>
            Join the<br />
            <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>simulator.</span><br />
            Skip the practice apps.
          </h1>
          <p className="text-base mt-5 max-w-md leading-relaxed" style={{ color: T.textMuted }}>
            Your first AI Speaking session, mock test and band prediction are free. No credit card. No catch.
          </p>

          <ul className="mt-8 space-y-3 max-w-md">
            {BENEFITS.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm" style={{ color: T.text }}>
                <span className="w-5 h-5 rounded-sm flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: T.primary, color: T.textOnPrimary, boxShadow: `0 0 8px ${T.primaryGlow}` }}>
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative mono text-[10px] tracking-[0.3em] flex items-center gap-2" style={{ color: T.textMuted }}>
          <Globe className="w-3 h-3" />
          <span>27,400+ candidates training in 27 countries</span>
        </div>
      </div>

      {/* ============================================================
          RIGHT — signup form
      ============================================================ */}
      <div className="relative flex items-center justify-center p-6 sm:p-10 lg:p-12">
        <div className="absolute inset-0 lg:hidden">
          <AIPropsBackground />
        </div>

        <div className="relative w-full max-w-lg">
          {/* Mobile brand */}
          <div className="lg:hidden mb-8">
            <AuthBrand />
          </div>

          <Link to="/design-preview/landing" onClick={() => play("hover")}
            className="inline-flex items-center gap-1 mono text-[10px] tracking-[0.3em] hover:underline mb-5"
            style={{ color: T.textMuted }} data-testid="back-to-landing">
            <ChevronLeft className="w-3 h-3" /> BACK TO HOME
          </Link>

          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-6 lg:p-8" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="flex items-center justify-between mb-2">
                <div className="mono text-[10px] tracking-[0.3em]" style={{ color: T.success }}>// CREATE PROFILE</div>
                <div className="mono text-[10px] tracking-[0.3em] inline-flex items-center gap-1" style={{ color: T.primary }}>
                  <Sparkles className="w-3 h-3" /> FREE FOREVER PLAN
                </div>
              </div>
              <h2 className="display text-4xl font-bold mb-2" style={{ color: T.text }}>Create your profile</h2>
              <p className="mono text-xs mb-7" style={{ color: T.textMuted }}>Takes 30 seconds. No credit card.</p>

              <form onSubmit={(e) => e.preventDefault()} className="space-y-5">
                <NeonInput label="Full Name" icon={User} type="text" placeholder="Alex Kaur" data-testid="signup-name" />
                <NeonInput label="Email Address" icon={Mail} type="email" placeholder="alex@quickmail.com" data-testid="signup-email" />
                <NeonInput label="Password" icon={Lock} type="password" placeholder="Min 8 characters" data-testid="signup-password" />

                {/* Target exam picker */}
                <div>
                  <div className="mono text-[10px] uppercase tracking-[0.3em] mb-2 font-bold" style={{ color: T.primary }}>
                    Target Exam
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {TARGET_EXAMS.map((e) => {
                      const active = exam === e.id;
                      return (
                        <button
                          key={e.id}
                          type="button"
                          onClick={() => { play("primary"); setExam(e.id); }}
                          data-testid={`signup-exam-${e.id.toLowerCase()}`}
                          className="relative h-14 rounded-sm transition-all duration-300 active:scale-[0.97]"
                          style={{
                            background: active ? T.primary : T.cardBg,
                            color: active ? T.textOnPrimary : T.text,
                            border: `2px solid ${active ? T.primary : T.border}`,
                            boxShadow: active ? `0 0 14px ${T.primaryGlow}` : "none",
                            fontFamily: "'Rajdhani', sans-serif",
                          }}
                        >
                          <span className="font-bold tracking-[0.15em] text-sm">{e.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <label className="flex items-start gap-2.5 cursor-pointer text-xs" style={{ color: T.textMuted, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  <input type="checkbox" defaultChecked className="w-4 h-4 rounded-sm cursor-pointer mt-0.5 shrink-0" style={{ accentColor: T.primary }} />
                  <span>
                    I agree to EnglishTest.UK&apos;s{" "}
                    <Link to="/" className="underline" style={{ color: T.text }}>Terms of Service</Link> and{" "}
                    <Link to="/" className="underline" style={{ color: T.text }}>Privacy Policy</Link>.
                  </span>
                </label>

                <PrimaryBtn type="submit" className="w-full h-16 text-base" data-testid="signup-submit">
                  <GraduationCap className="w-5 h-5" />
                  START YOUR FREE ASSESSMENT
                  <ArrowRight className="w-5 h-5" />
                </PrimaryBtn>

                <div className="mono text-[10px] tracking-[0.3em] text-center" style={{ color: T.textMuted }}>
                  // NO CREDIT CARD · 5-MIN AI BAND ESTIMATE · INSTANT RESULT
                </div>
              </form>

              <div className="text-sm text-center mt-7" style={{ color: T.textMuted, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Already have a profile?{" "}
                <Link to="/design-preview/login" onClick={() => play("hover")} className="font-semibold" style={{ color: T.primary }}>
                  Sign in →
                </Link>
              </div>
            </div>
          </HudFrame>

          {/* Trust footer */}
          <div className="mt-6 flex items-center justify-between mono text-[9px] tracking-[0.3em]" style={{ color: T.textMuted }}>
            <span>// 256-BIT TLS · GDPR · SOC-2 READY</span>
            <span>v2.6 · UK</span>
          </div>
        </div>
      </div>
    </div>
  );
}
